/**
 * ステッカー位置調整ユーティリティ + FV ステッカーの右マージン確保
 *
 * - getStickerRightLimit(): ビューポート右端から --padding-x 引いた値（クランプ上限）
 * - .mv-sticker / .hero-sticker-wrap の右端が --padding-x 未満にならないよう left を再計算
 */

export function getStickerRightLimit() {
  // --padding-x は rem 指定（例: 24rem）で、このプロジェクトの rem-vw 換算システムでは
  // 基準幅（PC 1440px / SP 375px）以外では 1rem ≠ 1px になる。
  // getPropertyValue はカスタムプロパティの生の値（文字列 "24rem"）を返すだけで
  // px には解決されないため、html の実際の font-size(px) を使って自前で換算する
  // （placeMvStickerVertical() と同じ方式）。ここがズレると、CSS の
  // right: var(--padding-x) で正確に配置されているステッカーに対しても
  // clampFvSticker が誤って left を上書きし、正しい配置を壊してしまう。
  const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 1;
  const paddingXRem = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--padding-x')) || 0;
  return window.innerWidth - paddingXRem * remPx;
}

/**
 * 要素自身の box right と全子孫の right の最大値を返す。
 * getBoundingClientRect() は position: relative + left 等で右にはみ出た子孫を含まないため、
 * 視覚的な右端を取得するには子孫を辿る必要がある。
 * （text-sticker は layout-zigzag/flow で .text-sticker-blocks が左右にオフセットされる）
 */
export function getVisualRight(el) {
  let max = el.getBoundingClientRect().right;
  el.querySelectorAll('*').forEach(child => {
    const r = child.getBoundingClientRect().right;
    if (r > max) max = r;
  });
  return max;
}

/** computed transform 文字列から X 方向のスケール値を取り出す（none は 1） */
function getScaleX(transform) {
  if (!transform || transform === 'none') return 1;
  const m = transform.match(/matrix\(([^)]+)\)/);
  if (m) return parseFloat(m[1].split(',')[0]) || 1;
  const m3 = transform.match(/matrix3d\(([^)]+)\)/);
  if (m3) return parseFloat(m3[1].split(',')[0]) || 1;
  return 1;
}

/**
 * ステッカー（transform: scale() が効いている要素）を利用可能幅に収める。
 * テキストは white-space: nowrap で 1 行固定のため、長いと幅が画面を超えて見切れる。
 * CSS の基準スケールを超えて拡大はせず、はみ出す場合のみ縮小する。
 *
 * el: scale が効いている要素（.text-sticker など）
 * availableWidth: 視覚的に許容する最大幅(px)
 */
export function fitStickerScale(el, availableWidth) {
  // 前回の inline transform をクリアして CSS 基準スケールで測定する
  el.style.transform = '';
  const baseScale = getScaleX(getComputedStyle(el).transform);
  const visualWidth = getVisualRight(el) - el.getBoundingClientRect().left; // 基準スケール適用後の視覚幅
  if (visualWidth <= 0 || baseScale <= 0 || availableWidth <= 0) return;

  const naturalWidth = visualWidth / baseScale; // scale 1 相当の自然幅
  const finalScale = Math.min(baseScale, availableWidth / naturalWidth);
  if (finalScale < baseScale) {
    // transform-origin は CSS の指定（normal=top left / large SP=bottom left）をそのまま使う
    el.style.transform = `scale(${finalScale})`;
  }
}

/**
 * 固定配置の FV ステッカー（.mv-sticker / .hero-sticker-wrap）の
 * 視覚的な右端がビューポート右端から --padding-x 以上のマージンを保つように left を調整。
 */
function clampFvSticker(sticker) {
  const inner = sticker.querySelector('.text-sticker') || sticker;

  // 前回 inline で設定した left をクリアして CSS デフォルト位置で測定
  sticker.style.left = '';
  const overshoot = getVisualRight(inner) - getStickerRightLimit();
  // fitStickerScale で幅は available 以内に収めてあるため、
  // 右マージン確保のため left を左へずらしても左端は --padding-x 以内に収まる
  if (overshoot > 0) {
    const cssLeft = parseFloat(getComputedStyle(sticker).left);
    sticker.style.left = `${cssLeft - overshoot}px`;
  }
  // 位置が確定してから表示（CSS 側で .is-placed まで opacity:0 にしてちらつき防止）
  sticker.classList.add('is-placed');
}

/**
 * ステッカーの外枠（.mv-sticker / .hero-sticker-wrap）自身のレイアウトボックスは
 * transform: scale() の影響を受けないため、既定では中の .text-sticker の見た目
 * （scale後）より小さいままになる。translate(-50%, -50%) によるセンタリングが
 * この小さいボックス基準で計算されズレるため、実測した見た目サイズを外枠の
 * width/height に反映する。.text-sticker 側の transform や見た目は変更しない。
 * （.text-sticker--large 側は width: max-content で自然幅に固定し、外枠の
 * width が width:100% 経由で逆流してサイズが膨張する連鎖を防いでいる）
 */
function sizeStickerWrapToVisual(sticker) {
  const inner = sticker.querySelector('.text-sticker');
  if (!inner) return;

  // 前回セットした値をクリアしてから自然サイズで測定する
  sticker.style.width = '';
  sticker.style.height = '';

  const rect = inner.getBoundingClientRect();
  let maxRight = rect.right;
  let maxBottom = rect.bottom;
  inner.querySelectorAll('*').forEach(child => {
    const r = child.getBoundingClientRect();
    if (r.right > maxRight) maxRight = r.right;
    if (r.bottom > maxBottom) maxBottom = r.bottom;
  });

  sticker.style.width = `${maxRight - rect.left}px`;
  sticker.style.height = `${maxBottom - rect.top}px`;
}

/**
 * SP（≤700px）でトップ FV の .mv-sticker を
 * text-fit（DANC ロゴ）下端と .university 上端の間の余白帯に縦配置する。
 * text-fit は上下中央基準、.university は下端基準のため両者の間隔は画面比率で変わる。
 * top:50% 基準の固定配置だと低い画面で .university に重なるので、
 * 実測したギャップの中央へ視覚コンテンツを置き、.university との重なりは最優先で回避する。
 * PC では inline top を消して CSS（top:75%）に委ねる。
 */
function placeMvStickerVertical(sticker) {
  const mv = sticker.closest('.mv');
  if (!mv) return;

  // PC は CSS に戻す
  if (window.innerWidth > 700) {
    sticker.style.top = '';
    return;
  }

  const textfit = mv.querySelector('.text-fit');
  const uni = mv.querySelector('.university');
  const inner = sticker.querySelector('.text-sticker') || sticker;
  if (!textfit || !uni) return;

  // 自然位置（CSS の top）で計測するため一旦 inline top をクリア
  sticker.style.top = '';

  const mvTop = mv.getBoundingClientRect().top;
  const gapTop = textfit.getBoundingClientRect().bottom - mvTop; // ロゴ下端
  const gapBottom = uni.getBoundingClientRect().top - mvTop;     // 大学名上端
  const boxTop = sticker.getBoundingClientRect().top - mvTop;
  const visRect = inner.getBoundingClientRect();
  const visTop = visRect.top - mvTop;
  const visH = visRect.height;
  // 縮小基準(bottom)によりボックス上部に生じる空き。これを差し引いて top を決める
  const emptyAbove = visTop - boxTop;

  const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 1;
  const margin = 24 * remPx; // ロゴ／大学名から確保する余白（24rem 相当）

  // ギャップ中央に視覚コンテンツを置く
  let desiredVisTop = (gapTop + gapBottom) / 2 - visH / 2;
  // text-fit 側の余白
  if (desiredVisTop < gapTop + margin) desiredVisTop = gapTop + margin;
  // .university 側の余白（最後に適用し重なりを最優先で回避）
  const maxVisTop = gapBottom - margin - visH;
  if (desiredVisTop > maxVisTop) desiredVisTop = maxVisTop;

  sticker.style.top = `${desiredVisTop - emptyAbove}px`;
}

function applyFvClamps() {
  // 1) まず画面幅に収まるよう縮小する。
  //    縮小すると高さも変わるため、縦配置(placeMvStickerVertical)より前に行う。
  //    available = ビューポート幅 - 左右 --padding-x（右マージン確保後に左端も余白内に収まる）
  const rightLimit = getStickerRightLimit();
  const paddingX = window.innerWidth - rightLimit;
  const available = rightLimit - paddingX;
  document.querySelectorAll('.mv-sticker, .hero-sticker-wrap').forEach(sticker => {
    const inner = sticker.querySelector('.text-sticker') || sticker;
    fitStickerScale(inner, available);
  });

  // 2) 外枠のボックスサイズを見た目（scale後）に合わせる
  document.querySelectorAll('.mv-sticker, .hero-sticker-wrap').forEach(sizeStickerWrapToVisual);

  // 3) 縦配置 → 4) 右端クランプ
  document.querySelectorAll('.mv-sticker').forEach(placeMvStickerVertical);
  document.querySelectorAll('.mv-sticker, .hero-sticker-wrap').forEach(clampFvSticker);
}

if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(applyFvClamps);
} else {
  window.addEventListener('load', applyFvClamps);
}

let resizeTimer;
let lastWidth = window.innerWidth;
window.addEventListener('resize', () => {
  if (window.innerWidth === lastWidth) return;
  lastWidth = window.innerWidth;
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(applyFvClamps, 200);
});
